import React, { useEffect, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import {
  CalendarIcon,
  TimeIcon,
  CheckCircleIcon,
  InfoOutlineIcon,
  ExternalLinkIcon,
} from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { CustomToast } from "../../components";
import { useFetch } from "../../hooks/useFetch";
import { useAuth } from "../../hooks/useAuth";
import { showDate, showTime } from "../../utilities/helper";
import * as routesNames from "../../constant/routes";

export interface TaskNotificationDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  taskId?: number | null;
  taskData?: any | null;
  onTaskSaved?: () => void;
}

export interface IWorkLogDetail {
  id: number;
  workDate: string;
  startTime: string;
  endTime: string;
  duration: number;
  description: string;
  status: number;
  approvalStatusName?: string;
}

// Priority styling helper
export const getTaskPriorityStyle = (priority?: string) => {
  switch (priority?.toLowerCase()) {
    case "high":
    case "urgent":
      return { bg: "#FFE4E4", color: "#F04444", border: "#FCA5A5" };
    case "medium":
    case "normal":
      return { bg: "#FFF2D6", color: "#D98A00", border: "#FCD34D" };
    case "low":
      return { bg: "#DDF8EC", color: "#12A866", border: "#86EFAC" };
    default:
      return { bg: "#F2F4F7", color: "#475467", border: "#E4E7EC" };
  }
};

// Status styling helper
export const getTaskStatusStyle = (status?: string) => {
  switch (status?.toLowerCase()) {
    case "completed":
    case "approved":
    case "done":
      return { bg: "#D1FAE5", color: "#16A34A", border: "#A7F3D0" };
    case "in progress":
    case "running":
      return { bg: "#DBEAFE", color: "#1557D6", border: "#BFDBFE" };
    case "in review":
    case "pending review":
    case "worklog review":
      return { bg: "#EDE9FE", color: "#7C3AED", border: "#DDD6FE" };
    case "overdue":
    case "action required":
    case "rejected":
      return { bg: "#FFE4E4", color: "#EF4444", border: "#FECACA" };
    case "upcoming":
    case "to do":
    case "not started":
    default:
      return { bg: "#F1F5F9", color: "#64748B", border: "#E2E8F0" };
  }
};

const formatTimeVal = (time?: string) => {
  if (!time) return "-";
  const val = String(time).trim();
  if (val.length === 5 && val.includes(":")) {
    const [h, m] = val.split(":").map(Number);
    if (!isNaN(h) && !isNaN(m)) {
      const d = new Date();
      d.setHours(h, m, 0, 0);
      return d.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  }
  return showTime(time);
};

const TaskNotificationDetails: React.FC<TaskNotificationDetailsProps> = ({
  isOpen,
  onClose,
  taskId,
  taskData,
}) => {
  const navigate = useNavigate();
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const { loginObj } = useAuth();

  const [loading, setLoading] = useState(false);
  const [worklogsLoading, setWorklogsLoading] = useState(false);
  const [worklogs, setWorklogs] = useState<IWorkLogDetail[]>([]);
  const [currentTask, setCurrentTask] = useState<any>(taskData || null);

  // Normalize task properties
  const normalizedTask = React.useMemo(() => {
    const src = currentTask || taskData || {};
    return {
      id: src.taskId ?? src.id ?? src.taskManagementTaskId ?? taskId ?? 0,
      taskId: src.taskId ?? src.id ?? src.taskManagementTaskId ?? taskId ?? 0,
      projectId: src.projectId ?? src.ProjectId ?? null,
      projectName: src.projectName ?? src.ProjectName ?? "Project",
      taskName:
        src.taskName ??
        src.projectTaskName ??
        src.title ??
        src.TaskName ??
        src.ProjectTaskName ??
        "Task Details",
      serviceName: src.serviceName ?? src.ServiceName ?? "-",
      milestoneName: src.milestoneName ?? src.MilestoneName ?? "-",
      taskTypeName: src.taskTypeName ?? src.TaskTypeName ?? "-",
      priorityName:
        src.priorityName ?? src.PriorityName ?? src.priority ?? "Normal",
      assignedToName:
        src.assignedToName ??
        src.AssignedToName ??
        src.assigneeName ??
        src.AssigneeName ??
        "-",
      reviewerName: src.reviewerName ?? src.ReviewerName ?? "-",
      statusName:
        src.statusName ??
        src.StatusName ??
        src.status ??
        src.Status ??
        "Pending",
      scheduleTypeName: src.scheduleTypeName ?? src.ScheduleTypeName ?? "-",
      date:
        src.date ??
        src.taskDate ??
        src.startDate ??
        src.Date ??
        src.TaskDate ??
        src.StartDate ??
        "",
      startTime: src.startTime ?? src.StartTime ?? "",
      endTime: src.endTime ?? src.EndTime ?? "",
      allocatedHours: src.allocatedHours ?? src.AllocatedHours ?? 0,
      estimatedHours: src.estimatedHours ?? src.EstimatedHours ?? 0,
      actualHours: src.actualHours ?? src.ActualHours ?? 0,
      description:
        src.description ??
        src.message ??
        src.Description ??
        src.Message ??
        "",
      workLogCount: src.workLogCount ?? src.WorkLogCount ?? 0,
    };
  }, [currentTask, taskData, taskId]);

  // Load task worklogs
  const loadWorkLogs = async (tId: number) => {
    if (!tId) return;
    setWorklogsLoading(true);
    try {
      const response = await fetchApi(
        `TaskManagement/GetWorkLogByTaskId?taskManagementTaskId=${tId}`,
        "GET",
        null,
        null,
        ""
      );

      if (response?.success && Array.isArray(response?.data)) {
        const mapped: IWorkLogDetail[] = response.data.map((item: any) => ({
          id: Number(item.id),
          workDate: item.workDate ? String(item.workDate).substring(0, 10) : "",
          startTime: item.startTime
            ? String(item.startTime).substring(0, 5)
            : "",
          endTime: item.endTime ? String(item.endTime).substring(0, 5) : "",
          duration: Number(item.durationHours ?? item.duration ?? 0),
          description: item.description ?? "",
          status: Number(item.approvalStatus ?? 1),
          approvalStatusName:
            Number(item.approvalStatus) === 2
              ? "Approved"
              : Number(item.approvalStatus) === 3
              ? "Rejected"
              : "Pending",
        }));
        setWorklogs(mapped);
      } else {
        setWorklogs([]);
      }
    } catch {
      setWorklogs([]);
    } finally {
      setWorklogsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (taskData) {
        setCurrentTask(taskData);
        const tId = taskData.taskManagementTaskId ?? taskData.id ?? taskData.projectTaskId ?? taskId;
        if (tId) {
          loadWorkLogs(tId);
        }
      } else if (taskId) {
        loadWorkLogs(taskId);
      }
    } else {
      setWorklogs([]);
    }
  }, [isOpen, taskId, taskData]);

  const priorityStyle = getTaskPriorityStyle(normalizedTask.priorityName);
  const statusStyle = getTaskStatusStyle(normalizedTask.statusName);

  const totalLoggedDuration = React.useMemo(() => {
    return worklogs
      .reduce((sum, item) => sum + Number(item.duration || 0), 0)
      .toFixed(2);
  }, [worklogs]);

  const handleNavigateToProject = () => {
    onClose();
    if (normalizedTask.projectId) {
      navigate(`${routesNames.PROJECTDETAIL}?id=${normalizedTask.projectId}`);
    } else {
      navigate(routesNames.TASKMANAGEMENT);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(3px)" />
      <ModalContent
        borderRadius="16px"
        overflow="hidden"
        boxShadow="0 20px 40px rgba(0,0,0,0.15)"
        border="1px solid #E2E8F0"
      >
        {/* MODAL HEADER */}
        <ModalHeader
          bg="#F8FAFC"
          borderBottom="1px solid #E2E8F0"
          py={4}
          px={6}
        >
          <Flex justify="space-between" align="center" pr={8}>
            <Box>
              <Flex align="center" gap={2} mb={1}>
                <Text fontSize="12px" fontWeight="600" color="#6366F1" textTransform="uppercase" letterSpacing="0.5px">
                  Task Notification Detail
                </Text>
                {normalizedTask.projectName && normalizedTask.projectName !== "-" && (
                  <Badge
                    bg="#EEF2FF"
                    color="#4F46E5"
                    fontSize="11px"
                    px={2}
                    py={0.5}
                    borderRadius="6px"
                    fontWeight="600"
                  >
                    {normalizedTask.projectName}
                  </Badge>
                )}
              </Flex>
              <Heading size="md" color="#1E293B" fontWeight="700">
                {normalizedTask.taskName}
              </Heading>
            </Box>

            <Flex align="center" gap={2}>
              <Badge
                bg={priorityStyle.bg}
                color={priorityStyle.color}
                border="1px solid"
                borderColor={priorityStyle.border}
                px={3}
                py={1}
                borderRadius="full"
                fontSize="12px"
                fontWeight="600"
              >
                {normalizedTask.priorityName} Priority
              </Badge>

              <Badge
                bg={statusStyle.bg}
                color={statusStyle.color}
                border="1px solid"
                borderColor={statusStyle.border}
                px={3}
                py={1}
                borderRadius="full"
                fontSize="12px"
                fontWeight="600"
              >
                {normalizedTask.statusName}
              </Badge>
            </Flex>
          </Flex>
        </ModalHeader>

        <ModalCloseButton top="18px" right="20px" />

        {/* MODAL BODY */}
        <ModalBody p={6} bg="#FFFFFF">
          {loading ? (
            <Flex justify="center" align="center" minH="250px">
              <Spinner size="xl" color="#4F46E5" thickness="3px" />
            </Flex>
          ) : (
            <Tabs variant="soft-rounded" colorScheme="indigo">
              <TabList mb={4} gap={2}>
                <Tab
                  _selected={{ bg: "#4F46E5", color: "white" }}
                  bg="#F1F5F9"
                  color="#475467"
                  fontSize="13px"
                  fontWeight="600"
                  borderRadius="8px"
                  px={4}
                  py={1.5}
                >
                  Overview & Details
                </Tab>
                <Tab
                  _selected={{ bg: "#4F46E5", color: "white" }}
                  bg="#F1F5F9"
                  color="#475467"
                  fontSize="13px"
                  fontWeight="600"
                  borderRadius="8px"
                  px={4}
                  py={1.5}
                >
                  Work Logs ({worklogs.length})
                </Tab>
              </TabList>

              <TabPanels>
                {/* TAB 1: OVERVIEW */}
                <TabPanel p={0}>
                  {/* Top Key Info Cards */}
                  <Grid
                    templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }}
                    gap={3}
                    mb={5}
                  >
                    <Box
                      p={3}
                      bg="#F8FAFC"
                      borderRadius="10px"
                      border="1px solid #E2E8F0"
                    >
                      <Text fontSize="11px" color="#64748B" fontWeight="600" textTransform="uppercase">
                        Service
                      </Text>
                      <Text fontSize="13px" fontWeight="600" color="#1E293B" mt={1}>
                        {normalizedTask.serviceName}
                      </Text>
                    </Box>

                    <Box
                      p={3}
                      bg="#F8FAFC"
                      borderRadius="10px"
                      border="1px solid #E2E8F0"
                    >
                      <Text fontSize="11px" color="#64748B" fontWeight="600" textTransform="uppercase">
                        Milestone
                      </Text>
                      <Text fontSize="13px" fontWeight="600" color="#1E293B" mt={1}>
                        {normalizedTask.milestoneName}
                      </Text>
                    </Box>

                    <Box
                      p={3}
                      bg="#F8FAFC"
                      borderRadius="10px"
                      border="1px solid #E2E8F0"
                    >
                      <Text fontSize="11px" color="#64748B" fontWeight="600" textTransform="uppercase">
                        Task Type
                      </Text>
                      <Text fontSize="13px" fontWeight="600" color="#1E293B" mt={1}>
                        {normalizedTask.taskTypeName}
                      </Text>
                    </Box>

                    <Box
                      p={3}
                      bg="#F8FAFC"
                      borderRadius="10px"
                      border="1px solid #E2E8F0"
                    >
                      <Text fontSize="11px" color="#64748B" fontWeight="600" textTransform="uppercase">
                        Schedule Type
                      </Text>
                      <Text fontSize="13px" fontWeight="600" color="#1E293B" mt={1}>
                        {normalizedTask.scheduleTypeName}
                      </Text>
                    </Box>
                  </Grid>

                  {/* Two Column Detailed Box */}
                  <Grid
                    templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                    gap={4}
                    mb={5}
                  >
                    {/* People & Assignment */}
                    <Box
                      p={4}
                      borderRadius="12px"
                      border="1px solid #E2E8F0"
                      bg="#FFFFFF"
                    >
                      <Heading size="xs" color="#475467" textTransform="uppercase" mb={3} letterSpacing="0.5px">
                        People & Assignment
                      </Heading>

                      <Flex direction="column" gap={3}>
                        <Flex justify="space-between" align="center">
                          <Text fontSize="13px" color="#64748B">
                            Assigned To
                          </Text>
                          <Flex align="center" gap={2}>
                            <Avatar size="xs" name={normalizedTask.assignedToName} bg="#6366F1" color="white" />
                            <Text fontSize="13px" fontWeight="600" color="#1E293B">
                              {normalizedTask.assignedToName}
                            </Text>
                          </Flex>
                        </Flex>

                        <Divider borderColor="#F1F5F9" />

                        <Flex justify="space-between" align="center">
                          <Text fontSize="13px" color="#64748B">
                            Reviewer
                          </Text>
                          <Flex align="center" gap={2}>
                            {normalizedTask.reviewerName && normalizedTask.reviewerName !== "-" ? (
                              <>
                                <Avatar size="xs" name={normalizedTask.reviewerName} bg="#10B981" color="white" />
                                <Text fontSize="13px" fontWeight="600" color="#1E293B">
                                  {normalizedTask.reviewerName}
                                </Text>
                              </>
                            ) : (
                              <Text fontSize="13px" color="#94A3B8">
                                Not assigned
                              </Text>
                            )}
                          </Flex>
                        </Flex>
                      </Flex>
                    </Box>

                    {/* Schedule & Hours */}
                    <Box
                      p={4}
                      borderRadius="12px"
                      border="1px solid #E2E8F0"
                      bg="#FFFFFF"
                    >
                      <Heading size="xs" color="#475467" textTransform="uppercase" mb={3} letterSpacing="0.5px">
                        Timing & Hours
                      </Heading>

                      <Flex direction="column" gap={3}>
                        <Flex justify="space-between" align="center">
                          <Flex align="center" gap={1.5} color="#64748B">
                            <CalendarIcon boxSize={3.5} />
                            <Text fontSize="13px">Task Date</Text>
                          </Flex>
                          <Text fontSize="13px" fontWeight="600" color="#1E293B">
                            {normalizedTask.date ? showDate(normalizedTask.date) : "-"}
                          </Text>
                        </Flex>

                        <Divider borderColor="#F1F5F9" />

                        {/* <Flex justify="space-between" align="center">
                          <Flex align="center" gap={1.5} color="#64748B">
                            <TimeIcon boxSize={3.5} />
                            <Text fontSize="13px">Time Window</Text>
                          </Flex>
                          <Text fontSize="13px" fontWeight="600" color="#1E293B">
                            {normalizedTask.startTime || normalizedTask.endTime
                              ? `${formatTimeVal(normalizedTask.startTime)} - ${formatTimeVal(normalizedTask.endTime)}`
                              : "-"}
                          </Text>
                        </Flex> */}

                        <Divider borderColor="#F1F5F9" />

                        <Flex justify="space-between" align="center">
                          <Text fontSize="13px" color="#64748B">
                            Hours (Est / Alloc / Actual)
                          </Text>
                          <Flex gap={2}>
                            <Badge bg="#F1F5F9" color="#475467" fontSize="11px">
                              Est: {normalizedTask.estimatedHours || 0}h
                            </Badge>
                            <Badge bg="#EFF6FF" color="#2563EB" fontSize="11px">
                              Alloc: {normalizedTask.allocatedHours || 0}h
                            </Badge>
                            <Badge bg="#ECFDF5" color="#059669" fontSize="11px">
                              Logged: {totalLoggedDuration || normalizedTask.actualHours || 0}h
                            </Badge>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Box>
                  </Grid>

                  {/* Description / Instructions */}
                  <Box
                    p={4}
                    borderRadius="12px"
                    border="1px solid #E2E8F0"
                    bg="#F8FAFC"
                  >
                    <Heading size="xs" color="#475467" textTransform="uppercase" mb={2} letterSpacing="0.5px">
                      Description / Instructions
                    </Heading>

                    {normalizedTask.description ? (
                      <Box
                        fontSize="13px"
                        color="#334155"
                        lineHeight="1.6"
                        maxH="150px"
                        overflowY="auto"
                        dangerouslySetInnerHTML={{ __html: normalizedTask.description }}
                      />
                    ) : (
                      <Text fontSize="13px" color="#94A3B8" fontStyle="italic">
                        No description provided for this task.
                      </Text>
                    )}
                  </Box>
                </TabPanel>

                {/* TAB 2: WORK LOGS */}
                <TabPanel p={0}>
                  {worklogsLoading ? (
                    <Flex justify="center" align="center" minH="180px">
                      <Spinner size="lg" color="#4F46E5" />
                    </Flex>
                  ) : worklogs.length === 0 ? (
                    <Box
                      p={8}
                      textAlign="center"
                      bg="#F8FAFC"
                      borderRadius="12px"
                      border="1px dashed #CBD5E1"
                    >
                      <InfoOutlineIcon boxSize={8} color="#94A3B8" mb={2} />
                      <Text fontSize="14px" fontWeight="600" color="#475467">
                        No Work Logs Recorded
                      </Text>
                      <Text fontSize="12px" color="#94A3B8" mt={1}>
                        No time logs have been submitted for this task yet.
                      </Text>
                    </Box>
                  ) : (
                    <Box borderRadius="12px" border="1px solid #E2E8F0" overflow="hidden">
                      <Table size="sm" variant="simple">
                        <Thead bg="#F8FAFC">
                          <Tr>
                            <Th color="#475467">S. No.</Th>
                            <Th color="#475467">Work Date</Th>
                            <Th color="#475467">Time</Th>
                            <Th color="#475467">Duration</Th>
                            <Th color="#475467">Status</Th>
                            <Th color="#475467">Description</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {worklogs.map((log, idx) => (
                            <Tr key={log.id || idx} _hover={{ bg: "#F8FAFC" }}>
                              <Td fontSize="13px">{idx + 1}</Td>
                              <Td fontSize="13px" fontWeight="500">
                                {showDate(log.workDate)}
                              </Td>
                              <Td fontSize="13px">
                                {formatTimeVal(log.startTime)} - {formatTimeVal(log.endTime)}
                              </Td>
                              <Td fontSize="13px" fontWeight="600" color="#4F46E5">
                                {log.duration} hrs
                              </Td>
                              <Td>
                                <Badge
                                  fontSize="11px"
                                  borderRadius="full"
                                  px={2}
                                  py={0.5}
                                  bg={
                                    log.status === 2
                                      ? "#D1FAE5"
                                      : log.status === 3
                                      ? "#FFE4E4"
                                      : "#FEF3C7"
                                  }
                                  color={
                                    log.status === 2
                                      ? "#16A34A"
                                      : log.status === 3
                                      ? "#DC2626"
                                      : "#D97706"
                                  }
                                >
                                  {log.approvalStatusName}
                                </Badge>
                              </Td>
                              <Td fontSize="13px" maxW="200px" isTruncated title={log.description}>
                                {log.description || "-"}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </ModalBody>

        {/* MODAL FOOTER */}
        <ModalFooter bg="#F8FAFC" borderTop="1px solid #E2E8F0" py={3} px={6} gap={3}>
          <Button
            variant="outline"
            size="sm"
            borderRadius="8px"
            borderColor="#D1D5DB"
            color="#374151"
            _hover={{ bg: "#F3F4F6" }}
            onClick={onClose}
          >
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TaskNotificationDetails;
